import classnames from "classnames";
import { observer } from "mobx-react";
import React, { useRef, useState } from "react";
import { ChatMessage as BaseChatMessage } from "@fidget/common/components/mstchat/chatMessage";
import { useStore } from "../../models";
import { ChatItemAssertion } from "../../models/room/chat";
import { IFakeUserTextChatItem, IUserTextChatItem } from "../../models/room/chat/items";
import { ChatIntent } from "./ChatIntent";
import styles from "./ChatMessage.css";

interface Props {
  item: IUserTextChatItem | IFakeUserTextChatItem;
  blurred?: boolean;
}

const INTENTS_OFFSET_RIGHT = 59;
const CLASSIFY_OFFSET_RIGHT = 8;
const SCREEN_WIDTH = 316;
/**
 * Chat Message component specialized for dashboard.
 */
export const ChatMessage = observer(({ item, blurred }: Props) => {
  const {
    intentStore: { suggestionsEnabled },
    roomStore
  } = useStore();
  const [divRef, setDivRef] = useState<HTMLDivElement>();
  // tslint:disable-next-line:no-null-keyword
  const messageRef = useRef<HTMLDivElement>(null);
  // tslint:disable-next-line:no-null-keyword
  const intentsRef = useRef<HTMLDivElement>(null);

  const { isBot = false, isAgent = false, isCurrent = false } = item.user as {
    isBot?: boolean;
    isAgent?: boolean;
    isCurrent?: boolean;
  };

  const renderIntent = ({ intents, user, text, id }: IUserTextChatItem) => {
    const shouldDisplayClassification = user?.isGuest && text;
    const intent = intents && intents.length > 0 && intents[0];

    const messageWidth = messageRef.current?.offsetWidth!;
    const intentsWidth = intentsRef.current?.offsetWidth!;

    const calculateMargins = (): any => {
      if (!intent) {
        return { alignSelf: "flex-end", marginRight: `-${CLASSIFY_OFFSET_RIGHT}px` };
      }

      if (intentsWidth > messageWidth + INTENTS_OFFSET_RIGHT) {
        return { alignSelf: "flex-start" };
      }

      if (!divRef || messageWidth === SCREEN_WIDTH) {
        return { alignSelf: "flex-end" };
      }
      const marginLeft = Math.min(
        messageWidth + INTENTS_OFFSET_RIGHT - intentsWidth,
        SCREEN_WIDTH - intentsWidth
      );

      return { alignSelf: "flex-end", marginLeft: `${marginLeft}px` };
    };

    return (
      <div className={styles.intents} style={calculateMargins()} ref={intentsRef}>
        {intent ? (
          <ChatIntent key={intent.type} intent={intent} messageId={id} parentRef={divRef} />
        ) : (
          shouldDisplayClassification && <ChatIntent messageId={id} parentRef={divRef} />
        )}
      </div>
    );
  };

  const renderUserTextChatMessage = (chatItem: IUserTextChatItem) => {
    const { text, attachments } = chatItem;

    const onFileDownload = (fileUrl: string, fileId?: string, fileName?: string) => {
      roomStore.currentRoom?.sendFileDownloaded(fileUrl, fileId, fileName);
    };

    const onLinkClicked = (link: string) => {
      roomStore.currentRoom?.sendLinkClicked(link);
    };

    return (
      <div
        className={classnames(styles.wrapper, { [styles.blurred]: blurred })}
        ref={(el) => setDivRef(el || undefined)}
      >
        {!!suggestionsEnabled && renderIntent(chatItem)}
        <div className={styles.message} ref={messageRef}>
          <BaseChatMessage
            text={text}
            fromBot={isBot}
            fromAgent={isAgent}
            alignRight={isCurrent}
            attachments={attachments}
            onFileDownload={onFileDownload}
            onLinkClicked={onLinkClicked}
          />
        </div>
      </div>
    );
  };

  const renderFakeUserTextChatMessage = ({ text }: IFakeUserTextChatItem) => {
    return (
      <div
        className={classnames(styles.wrapper, { [styles.blurred]: blurred })}
        ref={(el) => setDivRef(el || undefined)}
      >
        <BaseChatMessage text={text} fromBot={isBot} alignRight={isCurrent} />
      </div>
    );
  };

  if (ChatItemAssertion.isUserText(item)) {
    return renderUserTextChatMessage(item);
  }

  if (ChatItemAssertion.isFakeUserText(item)) {
    return renderFakeUserTextChatMessage(item);
  }

  return <></>;
});
